import type { Setup } from '@/shared/api/types'
import styles from './ProjectMap.module.css'

/**
 * Карта проекта.
 *
 * Показывает не проценты, а что именно сделано и что осталось. Проценты
 * сами по себе не говорят ничего: человек, увидев шестьдесят, всё равно
 * идёт искать, чего не хватает.
 */
interface ProjectMapProps {
  setup?: Setup
  /** Шаги, закрытые последним ходом настройщика: их строки вспыхивают. */
  flash?: string[]
  /** Номер хода. Меняет ключ строки, чтобы вспышка повторялась, даже
   * если два хода подряд трогали один и тот же шаг. */
  tick?: number
}

export function ProjectMap({ setup, flash = [], tick = 0 }: ProjectMapProps) {
  if (!setup) return null

  const done = setup.steps.filter((step) => step.done).length
  const percent = Math.round((done / setup.steps.length) * 100)

  return (
    <div className={styles.map}>
      <div className={styles.panel}>
        <div className={styles.head}>
          <span className={styles.title}>Карта проекта</span>
          <span className={styles.counter}>
            {done} из {setup.steps.length} · {percent}%
          </span>
        </div>

        <div className={styles.progress}>
          <div
            className={`${styles.progressFill} ${percent === 100 ? styles.progressDone : ''}`}
            style={{ width: `${percent}%` }}
          />
        </div>

        <div className={styles.steps}>
          {setup.steps.map((step, i) => (
            <div
              key={flash.includes(step.key) ? `${step.key}-${tick}` : step.key}
              className={`${styles.step} ${flash.includes(step.key) ? styles.stepFlash : ''}`}
            >
              <span
                className={[
                  styles.badge,
                  step.done ? styles.badgeDone : '',
                  step.key === setup.currentStep ? styles.badgeCurrent : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {step.done ? <CheckIcon /> : i + 1}
              </span>

              <span className={styles.stepBody}>
                <span className={styles.stepName}>{step.title}</span>
                <span className={`${styles.stepHint} ${step.done ? styles.stepDone : ''}`}>
                  {step.done
                    ? 'готово'
                    : step.key === setup.currentStep
                      ? step.hint
                      : 'пока не заполнено'}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.head}>
          <span className={styles.title}>Начало разговора</span>
          <span className={styles.counter}>{setup.openers.length}</span>
        </div>

        {setup.openers.length === 0 ? (
          <p className={styles.empty}>Пока нет ни одной стартовой фразы</p>
        ) : (
          setup.openers.map((opener, i) => (
            <div key={opener.id} className={styles.item}>
              <span className={styles.itemIndex}>{i + 1}</span>
              <span className={styles.itemText}>{opener.text}</span>
              {opener.primary && <span className={styles.primary}>первая</span>}
            </div>
          ))
        )}

        <p className={styles.link}>Изменить можно сообщением настройщику</p>
      </div>

      <div className={styles.panel}>
        <div className={styles.head}>
          <span className={styles.title}>Этапы разговора</span>
          <span className={styles.counter}>{setup.stages.length}</span>
        </div>

        {setup.stages.length === 0 ? (
          <p className={styles.empty}>Этапы ещё не описаны</p>
        ) : (
          setup.stages.map((stage, i) => (
            <div key={stage.id} className={styles.item}>
              <span className={styles.itemIndex}>{i + 1}</span>
              <span className={styles.itemText}>
                {stage.title}
                {stage.goal && <span className={styles.itemGoal}>{stage.goal}</span>}
              </span>
            </div>
          ))
        )}

        <p className={styles.link}>Изменить можно сообщением настройщику</p>
      </div>
    </div>
  )
}

function CheckIcon() {
  return (
    <svg width="9" height="7" viewBox="0 0 9 7" fill="none" aria-hidden="true">
      <path
        d="M1 3.6L3.3 5.8L8 1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
